import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Radio } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LiveSectionAdmin = ({ getAuthHeaders }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("LIVE FRA MESSEN");
  const [duration, setDuration] = useState(24);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/live-section`);
      setSettings(res.data);
      setTitle(res.data.title || "LIVE FRA MESSEN");
      setDuration(res.data.duration_hours || 24);
    } catch { /* defaults */ } finally { setLoading(false); }
  };

  const toggleLive = async (activate) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/live-section`, { is_active: activate, title, duration_hours: duration }, { headers: getAuthHeaders() });
      setSettings(res.data);
      toast.success(activate ? "Live sektion er nu aktiv!" : "Live sektion er deaktiveret");
    } catch { toast.error("Kunne ikke opdatere"); } finally { setSaving(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/live-section`, { is_active: settings?.is_active || false, title, duration_hours: duration }, { headers: getAuthHeaders() });
      setSettings(res.data);
      toast.success("Indstillinger gemt");
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    } catch { toast.error("Kunne ikke gemme"); } finally { setSaving(false); }
  };

  const isCurrentlyActive = () => {
    if (!settings?.is_active || !settings?.activated_at) return false;
    const start = new Date(settings.activated_at);
    const end = new Date(start.getTime() + (settings.duration_hours || 24) * 60 * 60 * 1000);
    return new Date() < end;
  };

  const getTimeRemaining = () => {
    if (!settings?.activated_at) return null;
    const start = new Date(settings.activated_at);
    const end = new Date(start.getTime() + (settings.duration_hours || 24) * 60 * 60 * 1000);
    const diff = end - new Date();
    if (diff <= 0) return "Udl\u00f8bet";
    const h = Math.floor(diff / (1000 * 60 * 60));
    const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${h}t ${m}m tilbage`;
  };

  if (loading) return <div className="text-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  const active = isCurrentlyActive();

  return (
    <div className="space-y-6" data-testid="live-section-admin">
      <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-4">LIVE SEKTION</h2>
      <div className={`mc-card p-6 border-l-4 ${active ? 'border-l-green-500' : 'border-l-gray-600'}`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${active ? 'bg-green-600/20' : 'bg-gray-600/20'}`}>
              <Radio className={`w-6 h-6 ${active ? 'text-green-400' : 'text-gray-500'}`} />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">{active ? "Live sektion er AKTIV" : "Live sektion er INAKTIV"}</h3>
              {active && <p className="text-green-400 text-sm">{getTimeRemaining()}</p>}
            </div>
          </div>
          <button onClick={() => toggleLive(!active)} disabled={saving}
            className={`px-6 py-3 rounded-lg font-['Oswald'] uppercase tracking-wide text-sm disabled:opacity-50 flex items-center gap-2 ${active ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-green-600 hover:bg-green-500 text-white'}`}
            data-testid="toggle-live-btn">
            {saving ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Gemmer...</> : active ? "Deaktiver" : "Aktiver Live"}
          </button>
        </div>
      </div>
      <div className="mc-card p-6">
        <h3 className="text-lg font-['Oswald'] font-bold text-white mb-4">Indstillinger</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Titel</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="mc-input w-full" data-testid="live-title-input" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Varighed (timer)</label>
            <div className="flex gap-2">
              {[12, 24, 48, 72].map(h => (
                <button key={h} type="button" onClick={() => setDuration(h)}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${duration === h ? 'bg-[var(--neon-blue)] text-black font-bold' : 'bg-neutral-800 text-gray-400 hover:bg-neutral-700'}`}>
                  {h}t
                </button>
              ))}
            </div>
          </div>
          <button onClick={saveSettings} disabled={saving || saved} className="chrome-btn px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2" data-testid="save-live-settings-btn">
            {saving ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Gemmer...</> : saved ? <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Gemt!</> : "Gem indstillinger"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveSectionAdmin;
